import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { useAiPanel } from '../../components/ai/AiPanelContext';
import { photosApi } from '../../api/photos.api';
import { journalApi } from '../../api/journal.api';
import type { JournalEntry, TripPhoto } from '../../types';
import { dayPlaceLabel } from '../../utils/tripLegs';
import { useAuthStore } from '../../stores/auth.store';
import { tripPermissions } from '../../utils/tripPermissions';
import { toast } from '../../components/ui/Toast';
import LoadingScreen from '../../components/ui/LoadingScreen';
import Spinner from '../../components/ui/Spinner';
import ConfirmModal from '../../components/ui/ConfirmModal';
import EmptyState from '../../components/ui/EmptyState';
import { DayStrip } from '../../components/trips/itinerary/DayStrip';
import { BookOpen, Icon, Upload } from '../../components/ui/Icon';
import { usePhotoUploads } from '../../hooks/usePhotoUploads';
import UploadProgressBar from '../../components/ui/UploadProgressBar';
import JournalDaySection from '../../components/journal/JournalDaySection';
import PhotoGrid from '../../components/journal/PhotoGrid';
import PhotoLightbox from '../../components/journal/PhotoLightbox';
import JournalViewToggle from '../../components/journal/JournalViewToggle';
import JournalPhotoMap from '../../components/journal/JournalPhotoMap';
import { overviewPhotoSequence, type JournalView } from '../../utils/journalViews';
import { stopAssignmentPatch } from '../../utils/photoSelection';
import { usePhotoBulkActions } from '../../hooks/usePhotoBulkActions';
import { PhotoSelectionProvider } from '../../components/journal/PhotoSelection';
import { usePhotoSelectionState } from '../../hooks/usePhotoSelectionState';
import PhotoSelectionBar from '../../components/journal/PhotoSelectionBar';
import StopSuggestionReview from '../../components/journal/StopSuggestionReview';
import { usePhotoStopSuggestions } from '../../hooks/usePhotoStopSuggestions';

/** ADR-0043: the diary replaces the gallery — each day, what was written and its photos. */
export default function JournalTab() {
  const { trip, days } = useTripContext();
  useAiPanel({ context: 'general' });
  const { t } = useTranslation();
  const me = useAuthStore(s => s.user);
  const { isOwner, canEdit } = tripPermissions(trip);

  const [photos, setPhotos] = useState<TripPhoto[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDayId, setActiveDayId] = useState<string | null>(null);
  const [view, setView] = useState<JournalView>('list');
  const [lightbox, setLightbox] = useState<{ among: TripPhoto[]; index: number } | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<TripPhoto | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<JournalEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { progress, uploading, uploadAll } = usePhotoUploads();
  const selection = usePhotoSelectionState();
  const bulk = usePhotoBulkActions(trip.id, setPhotos, setEntries);
  const stopSuggestions = usePhotoStopSuggestions(trip.id);
  const { keepOnly } = selection;

  useEffect(() => {
    Promise.all([photosApi.list(trip.id), journalApi.list(trip.id)])
      .then(([p, e]) => { setPhotos(p); setEntries(e); })
      .catch(() => toast(t('journal.loadError'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id]);

  const shownDays = useMemo(() => {
    if (activeDayId) return days.filter(d => d.id === activeDayId);
    const used = new Set([...photos.map(p => p.dayId), ...entries.map(e => e.dayId)]);
    return days.filter(d => used.has(d.id));
  }, [days, activeDayId, photos, entries]);
  const unassigned = useMemo(() => photos.filter(p => p.dayId === null), [photos]);
  const visiblePhotos = useMemo(
    () => (activeDayId ? photos.filter(p => p.dayId === activeDayId) : photos),
    [photos, activeDayId],
  );
  const overviewSequence = useMemo(
    () => overviewPhotoSequence(shownDays, entries, photos),
    [shownDays, entries, photos],
  );
  const selectedPhotos = useMemo(() => photos.filter(p => selection.ids.has(p.id)), [photos, selection.ids]);
  const allSelected = visiblePhotos.length > 0 && visiblePhotos.every(p => selection.ids.has(p.id));

  useEffect(() => { keepOnly(new Set(photos.map(p => p.id))); }, [photos, keepOnly]);

  function changeView(next: JournalView) {
    if (next === 'map') selection.clear();
    setView(next);
  }

  /** A batch that went through entirely ends the selection. */
  async function onBulk(work: Promise<boolean>) {
    if (await work) selection.clear();
  }

  async function uploadFiles(files: File[], dayId: string | null): Promise<TripPhoto[]> {
    const stored = await uploadAll(files, (photo, onProgress) =>
      photosApi.upload(trip.id, photo.file, dayId, photo.capture, onProgress));
    setPhotos(prev => [...prev, ...stored]);
    // ADR-0042: the capture date beats the filter, so some may land elsewhere.
    const placedElsewhere = stored.filter(p => p.dayId && p.dayId !== dayId).length;
    if (placedElsewhere > 0) toast(t('gallery.placedByDate', { count: placedElsewhere }), 'success');
    return stored;
  }

  async function handleHeaderUpload(list: FileList | null) {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    // Emptied first, so picking the same photos again still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    await uploadFiles(files, activeDayId);
  }

  /** The server moves chosen photos to the entry's day; mirror that here. */
  function applyEntry(entry: JournalEntry) {
    setEntries(prev => prev.some(e => e.id === entry.id) ? prev.map(e => e.id === entry.id ? entry : e) : [...prev, entry]);
    setPhotos(prev => prev.map(p => {
      if (entry.photoIds.includes(p.id)) {
        return p.dayId === entry.dayId
          ? { ...p, journalEntryId: entry.id }
          : { ...p, journalEntryId: entry.id, dayId: entry.dayId, activityId: null, activity: null };
      }
      return p.journalEntryId === entry.id ? { ...p, journalEntryId: null } : p;
    }));
  }

  async function handleCreate(dayId: string, body: string, photoIds: string[], shared: boolean) {
    try {
      applyEntry(await journalApi.create(trip.id, { dayId, body, photoIds, shared }));
    } catch (e) {
      toast(t('journal.saveError'), 'error');
      throw e;
    }
  }

  async function handleUpdate(entryId: string, body: string, photoIds: string[], shared: boolean) {
    try {
      applyEntry(await journalApi.update(trip.id, entryId, { body, photoIds, shared }));
    } catch (e) {
      toast(t('journal.saveError'), 'error');
      throw e;
    }
  }

  async function handleDeleteEntry() {
    if (!entryToDelete) return;
    setDeleting(true);
    try {
      await journalApi.remove(trip.id, entryToDelete.id);
      setEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      setPhotos(prev => prev.map(p => p.journalEntryId === entryToDelete.id ? { ...p, journalEntryId: null } : p));
      setEntryToDelete(null);
    } catch {
      toast(t('journal.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  }

  /** With all days shown the arrows run across days; on one day, within its section. */
  function openPhoto(photo: TripPhoto, section: TripPhoto[]) {
    const among = activeDayId === null ? overviewSequence : section;
    setLightbox({ among, index: among.findIndex(p => p.id === photo.id) });
  }

  async function handleDeletePhoto() {
    if (!photoToDelete) return;
    setDeleting(true);
    try {
      await photosApi.remove(trip.id, photoToDelete.id);
      setPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
      setEntries(prev => prev.map(e => ({ ...e, photoIds: e.photoIds.filter(id => id !== photoToDelete.id) })));
      setPhotoToDelete(null);
      setLightbox(null);
    } catch {
      toast(t('gallery.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  }

  async function handleMoveToDay(photo: TripPhoto, dayId: string | null) {
    try {
      const updated = await photosApi.update(trip.id, photo.id, { dayId });
      setPhotos(prev => prev.map(p => p.id === updated.id ? updated : p));
      setLightbox(null);
    } catch {
      toast(t('gallery.moveError'), 'error');
    }
  }

  async function handleChangeStop(photo: TripPhoto, activityId: string | null) {
    try {
      const updated = await photosApi.update(trip.id, photo.id, { activityId });
      setPhotos(prev => prev.map(p => p.id === updated.id ? updated : p));
      setLightbox(prev => prev && { ...prev, among: prev.among.map(p => p.id === updated.id ? updated : p) });
    } catch {
      toast(t('gallery.moveError'), 'error');
    }
  }

  if (loading) return <LoadingScreen />;

  const isEmpty = shownDays.length === 0 && (activeDayId !== null || unassigned.length === 0);

  return (
    <PhotoSelectionProvider value={canEdit ? selection.context : null}>
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
              {trip.destination.toUpperCase()} · {days.length} {t('itinerary.dayUnit', { count: days.length })}
            </p>
            <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
              {t('journal.title')}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <JournalViewToggle value={view} onChange={changeView} />
          {canEdit && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-primary text-sm gap-1.5 disabled:opacity-60 shrink-0"
              >
                {uploading ? <Spinner size="sm" /> : <Icon as={Upload} size={14} />}
                {uploading ? t('gallery.uploading') : t('gallery.upload')}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={e => handleHeaderUpload(e.target.files)} />
            </>
          )}
          </div>
        </div>
      </div>

      {/* The map fills the tab; only the list keeps a reading width. */}
      <div className={`flex-1 px-4 md:px-8 ${
        view === 'list' ? 'overflow-auto py-5 md:py-6' : 'min-h-0 overflow-auto flex flex-col py-4'
      }`}>
        <div className={view === 'list' ? 'max-w-3xl space-y-8' : 'flex-1 min-h-0 flex flex-col'}>
          {view === 'map' && (
            <JournalPhotoMap
              tripId={trip.id}
              days={days}
              photos={photos}
              onOpenPhoto={(among, index) => setLightbox({ among, index })}
            />
          )}
          {view === 'list' && <>
          {days.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1 -mb-1">
              <button
                onClick={() => setActiveDayId(null)}
                className={`flex items-center justify-center min-w-[52px] h-[60px] px-3 rounded-xl transition-all cursor-pointer shrink-0 text-xs font-semibold ${
                  activeDayId === null
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {t('gallery.allDays')}
              </button>
              <DayStrip days={days} activeId={activeDayId} onSelect={day => setActiveDayId(day.id)} />
            </div>
          )}

          {isEmpty && activeDayId === null ? (
            <EmptyState
              icon={BookOpen}
              title={t('journal.empty.title')}
              body={t('journal.empty.body')}
              action={canEdit ? { label: t('gallery.upload'), icon: Upload, onClick: () => fileInputRef.current?.click() } : undefined}
            />
          ) : (
            // Like the tickets' chronology, but only the days with something in them.
            <div>
            {shownDays.map((day, i, list) => (
              <JournalDaySection
                key={day.id}
                day={day}
                place={dayPlaceLabel(trip.destinations ?? [], day.dayNumber)}
                entries={entries.filter(e => e.dayId === day.id)}
                photos={photos.filter(p => p.dayId === day.id)}
                overview={activeDayId === null}
                last={i === list.length - 1}
                meId={me?.id}
                canEdit={canEdit}
                isOwner={isOwner}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDeleteEntry={setEntryToDelete}
                onUpload={uploadFiles}
                onOpenPhoto={openPhoto}
              />
            ))}
            </div>
          )}

          {activeDayId === null && unassigned.length > 0 && (
            <section className="space-y-3">
              <h3 className="font-serif italic font-semibold text-xl text-slate-900 dark:text-slate-100">{t('gallery.unassignedDay')}</h3>
              <PhotoGrid photos={unassigned} size="thumb" onOpen={photo => openPhoto(photo, unassigned)} />
            </section>
          )}
          </>}
        </div>
      </div>

      {selection.selecting && view === 'list' && (
        <PhotoSelectionBar
          count={selection.ids.size}
          allSelected={allSelected}
          days={days}
          busy={bulk.busy}
          onCancel={selection.clear}
          onToggleAll={() => selection.selectAll(allSelected ? [] : visiblePhotos.map(p => p.id))}
          onMoveToDay={dayId => onBulk(bulk.update(selectedPhotos, { dayId }))}
          onAssignStop={stop => onBulk(bulk.update(selectedPhotos, stop ? stopAssignmentPatch(stop) : { activityId: null }))}
          onSuggestStops={() => void stopSuggestions.suggest(selectedPhotos)}
          onDelete={() => onBulk(bulk.remove(selectedPhotos))}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          photos={lightbox.among}
          index={lightbox.index}
          days={days}
          canEdit={canEdit}
          onIndexChange={index => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
          onMoveToDay={handleMoveToDay}
          onChangeStop={handleChangeStop}
          onSuggestStop={photo => { setLightbox(null); void stopSuggestions.suggest([photo]); }}
          onDelete={setPhotoToDelete}
        />
      )}

      {stopSuggestions.review && (
        <StopSuggestionReview
          review={stopSuggestions.review}
          days={days}
          busy={bulk.busy}
          onClose={stopSuggestions.close}
          onApply={async accepted => {
            const patchOf = new Map(accepted.map(a => [a.photo.id, stopAssignmentPatch(a.stop)]));
            const ok = await bulk.update(accepted.map(a => a.photo), photo => patchOf.get(photo.id) ?? {});
            if (ok) { stopSuggestions.close(); selection.clear(); }
          }}
        />
      )}

      <UploadProgressBar progress={progress} />

      <ConfirmModal
        open={!!photoToDelete}
        title={t('gallery.deleteConfirm')}
        onConfirm={handleDeletePhoto}
        onCancel={() => setPhotoToDelete(null)}
        loading={deleting}
      />
      <ConfirmModal
        open={!!entryToDelete}
        title={t('journal.deleteConfirm')}
        onConfirm={handleDeleteEntry}
        onCancel={() => setEntryToDelete(null)}
        loading={deleting}
      />
    </div>
    </PhotoSelectionProvider>
  );
}
